import React from "react";
import { Link, useParams } from "react-router-dom";
import { news } from "../mock";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import CtaBand from "../components/CtaBand";

export default function NewsArticlePage() {
  const { id } = useParams();
  const article = news.find((n) => n.id === id);
  const others = news.filter((n) => n.id !== id).slice(0, 3);

  if (!article) {
    return (
      <section className="pt-40 pb-32 text-center">
        <div className="font-display text-3xl text-slate-900">Article not found</div>
        <Link to="/news" className="inline-flex items-center gap-2 mt-5 text-brand-700 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to all articles
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="pt-36 lg:pt-40">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/news" className="inline-flex items-center gap-1.5 text-[13px] text-slate-600 hover:text-brand-700">
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <div className="mt-5 inline-block px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-[12px] font-medium">
            {article.category}
          </div>
          <h1 className="font-display mt-4 text-3xl lg:text-5xl font-bold text-slate-900 leading-tight">
            {article.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 text-[13px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {article.date}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {article.readTime}</span>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
            <img src={article.image} alt={article.title} className="w-full h-[360px] lg:h-[480px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 prose prose-slate prose-lg">
          <p className="text-[18px] leading-relaxed text-slate-700">{article.excerpt}</p>
          <p className="mt-6 text-[16px] leading-relaxed text-slate-600">
            The IBM Power ecosystem moves carefully, but it moves. Every release cycle brings firmware changes, PowerVM enhancements, and AIX technology levels that materially change how we design and operate highly-available workloads. In this note we unpack what the most recent changes mean in practice, drawing on the engagements our team has just wrapped.
          </p>
          <h3 className="font-display mt-10 text-2xl font-semibold text-slate-900">What changed</h3>
          <p className="mt-3 text-[16px] leading-relaxed text-slate-600">
            We saw three clear shifts over the last six months: hardware densification on Power10, a push towards dual-VIOS shared storage pools as a default rather than the exception, and renewed customer interest in cross-site PowerHA topologies after a spate of regional outages.
          </p>
          <ul className="mt-4 space-y-2 text-[16px] text-slate-600">
            <li>• More workloads per frame with sensible entitlement overcommit</li>
            <li>• Shared Ethernet Adapter failover defaulting to load-sharing</li>
            <li>• GLVM-based replication for RPO &lt; 5 minutes</li>
            <li>• Tighter AIX security baselines aligned with client audit frameworks</li>
          </ul>
          <h3 className="font-display mt-10 text-2xl font-semibold text-slate-900">What to do about it</h3>
          <p className="mt-3 text-[16px] leading-relaxed text-slate-600">
            Start with a health check. If your design documentation hasn’t been touched in 18 months, the delta between what you think you run and what you actually run is almost certainly bigger than you’re comfortable with. We walk customers through a structured review — two days onsite, five days of writeup — and every customer ends with a concrete roadmap.
          </p>
          <p className="mt-6 text-[16px] leading-relaxed text-slate-600">
            Then plan your upgrades. Bundle TL/SP moves with firmware, line them up against your LPM maintenance windows and treat every change like the one that could take down production — because it probably could.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="font-display text-2xl font-semibold text-slate-900">Keep reading</div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {others.map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover-lift">
                <img src={n.image} alt={n.title} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="p-5">
                  <div className="text-[12px] text-brand-700 font-medium">{n.category}</div>
                  <h4 className="font-display mt-2 text-lg font-semibold text-slate-900 group-hover:text-brand-700 transition-colors">{n.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
